/**
 * Team Dashboard Page
 *
 * Manager view of team members with notes, agreements and POP management.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth';
import { useTeamMembers, TeamMember } from './useTeam';
import { useEvaluations, useCreateEvaluation, useDeleteEvaluation } from '../evaluations/useEvaluations';
import { useManagerNotes, useCreateManagerNote, useUpdateManagerNote, useDeleteManagerNote } from '../notes/useNotes';
import type { ManagerNote } from '../../lib/notesApi';
import { ScoreDisplay } from '../evaluations/wizard/ScoreInput';
import type { EvaluationStatus, Evaluation } from '../../lib/evaluationsApi';
import { useToast } from '../../components/Toast';
import { AgreementsTab } from './AgreementsTab';
import { TeamAgreementsOverview } from './TeamAgreementsOverview';
import { TeamEvaluationsCard } from './TeamEvaluationsCard';
import { TeamPeerEvaluationsCard } from './TeamPeerEvaluationsCard';
import { AssessmentReportsTab } from './AssessmentReportsTab';
import { TeamScheduleOverview } from '../werkrooster/components/TeamScheduleOverview';
import { NmbrsLeaveBalanceCard } from '../nmbrs/components/NmbrsLeaveBalanceCard';
import { canDeleteEvaluationsFor } from './evaluationPermissions';

type VisibilityType = 'private' | 'shared';

const STATUS_LABELS: Record<EvaluationStatus, { label: string; color: string }> = {
  draft: { label: 'Concept', color: 'bg-yellow-100 text-yellow-800' },
  submitted: { label: 'Ingediend', color: 'bg-blue-100 text-blue-800' },
  in_review: { label: 'In Review', color: 'bg-purple-100 text-purple-800' },
  final: { label: 'Definitief', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Geannuleerd', color: 'bg-gray-100 text-gray-600' },
};

export function TeamDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { data: teamMembers, isLoading: teamLoading } = useTeamMembers();
  const { data: evaluations, isLoading: evalLoading } = useEvaluations({ managerId: user?.id });
  const createEvaluation = useCreateEvaluation();
  const deleteEvaluation = useDeleteEvaluation();

  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [deleteConfirmEvaluation, setDeleteConfirmEvaluation] = useState<Evaluation | null>(null);
  const [activeTab, setActiveTab] = useState<'notes' | 'agreements' | 'pop' | 'evaluations' | 'assessment-reports'>('notes');
  const [memberSearch, setMemberSearch] = useState('');
  const [viewMode, setViewMode] = useState<'members' | 'werkrooster' | 'verlofsaldi'>('members');

  // Handle URL query parameters for pre-selecting employee and tab
  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      const employeeIdParam = searchParams.get('employeeId');
      const tabParam = searchParams.get('tab');

      // Handle view mode from URL
      if (tabParam === 'werkrooster') {
        setViewMode('werkrooster');
        setSearchParams({}, { replace: true });
      } else if (employeeIdParam) {
        const member = teamMembers.find(m => m.employeeId === parseInt(employeeIdParam));
        if (member) {
          setSelectedMember(member);
          if (tabParam && ['notes', 'agreements', 'pop', 'evaluations', 'assessment-reports'].includes(tabParam)) {
            setActiveTab(tabParam as typeof activeTab);
          }
          // Clear the query params after applying them
          setSearchParams({}, { replace: true });
        }
      }
    }
  }, [teamMembers, searchParams, setSearchParams]);
  const [statusFilter, _setStatusFilter] = useState<EvaluationStatus | 'all'>('all');
  const [showNewEvalForm, setShowNewEvalForm] = useState(false);
  const [newEvalEmployeeId, setNewEvalEmployeeId] = useState('');
  const [newEvalPeriode, setNewEvalPeriode] = useState({
    start: new Date().getFullYear() + '-01-01',
    end: new Date().getFullYear() + '-12-31',
  });

  // Notes for selected member (uses employeeId for API)
  const { data: notesResponse } = useManagerNotes(selectedMember?.employeeId);
  const notes = notesResponse?.data || [];
  const createNote = useCreateManagerNote();
  const updateNote = useUpdateManagerNote();
  const deleteNote = useDeleteManagerNote();

  // Note form state
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteVisibility, setNoteVisibility] = useState<VisibilityType>('private');
  const [noteContent, setNoteContent] = useState('');
  const [editingNote, setEditingNote] = useState<ManagerNote | null>(null);
  const [deleteConfirmNote, setDeleteConfirmNote] = useState<ManagerNote | null>(null);

  const filteredTeamMembers = useMemo(() => {
    if (!teamMembers || !memberSearch.trim()) return teamMembers;
    const query = memberSearch.toLowerCase();
    return teamMembers.filter(
      (m) => m.name.toLowerCase().includes(query) || m.email.toLowerCase().includes(query)
    );
  }, [teamMembers, memberSearch]);

  const isLoading = teamLoading || evalLoading;

  const filteredEvaluations = evaluations?.data?.filter((e) =>
    statusFilter === 'all' ? true : e.status === statusFilter
  ) || [];

  const memberEvaluations = selectedMember
    ? filteredEvaluations.filter((e) => e.employeeId === selectedMember.employeeId)
    : [];

  const handleCreateEvaluation = async () => {
    if (!newEvalEmployeeId) return;

    try {
      const result = await createEvaluation.mutateAsync({
        employeeId: parseInt(newEvalEmployeeId),
        periodeStart: newEvalPeriode.start,
        periodeEnd: newEvalPeriode.end,
      });
      setShowNewEvalForm(false);
      setNewEvalEmployeeId('');
      navigate(`/beoordeling/${result.id}/wizard`);
    } catch {
      // Error handled by mutation
    }
  };

  const handleSaveNote = async () => {
    if (!selectedMember || !noteContent.trim()) return;

    try {
      if (editingNote) {
        await updateNote.mutateAsync({
          id: editingNote.id,
          input: {
            content: noteContent,
            isPrivate: noteVisibility === 'private',
          },
        });
        setEditingNote(null);
        showToast('Notitie bijgewerkt', 'success');
      } else {
        await createNote.mutateAsync({
          employeeId: selectedMember.employeeId,
          content: noteContent,
          isPrivate: noteVisibility === 'private',
        });
        showToast(noteVisibility === 'shared' ? 'Gespreksverslag opgeslagen' : 'Notitie opgeslagen', 'success');
      }
      setNoteContent('');
      setShowNoteForm(false);
    } catch {
      showToast('Fout bij opslaan', 'error');
    }
  };

  const handleEditNote = (note: ManagerNote) => {
    setEditingNote(note);
    setNoteContent(note.content);
    setNoteVisibility(note.isPrivate ? 'private' : 'shared');
    setShowNoteForm(true);
  };

  const handleDeleteNote = async () => {
    if (!deleteConfirmNote) return;
    try {
      await deleteNote.mutateAsync(deleteConfirmNote.id);
      setDeleteConfirmNote(null);
      showToast('Notitie verwijderd', 'success');
    } catch {
      showToast('Fout bij verwijderen', 'error');
    }
  };

  const handleMemberClick = (member: TeamMember) => {
    setSelectedMember(member);
    setActiveTab('notes');
  };

  const handleDeleteEvaluation = async () => {
    if (!deleteConfirmEvaluation) return;
    try {
      await deleteEvaluation.mutateAsync(deleteConfirmEvaluation.id);
      setDeleteConfirmEvaluation(null);
      showToast('Beoordeling verwijderd', 'success');
    } catch {
      showToast('Fout bij verwijderen', 'error');
    }
  };

  // Expliciete lijst van rollen die mogen verwijderen; een onbekende rol
  // krijgt de knop niet (spec-fix-rolbepaling-app-scope, punt 4).
  const canDeleteEvaluations = canDeleteEvaluationsFor(user?.role);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Team laden...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="team-dashboard-page" className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Mijn Team</h1>
          <p className="mt-1 text-sm text-gray-500">
            Beheer notities, afspraken en beoordelingen voor je teamleden.
          </p>
        </div>

        {/* Overview Cards - Evaluations, Peer Evaluations and Agreements */}
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <TeamEvaluationsCard onCreateNew={() => setShowNewEvalForm(true)} />
          <TeamPeerEvaluationsCard />
          <TeamAgreementsOverview />
        </div>

        {/* View mode tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex -mb-px">
            <button
              onClick={() => setViewMode('members')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'members'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Teamleden
            </button>
            <button
              onClick={() => setViewMode('werkrooster')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'werkrooster'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Werkrooster
            </button>
            <button
              onClick={() => setViewMode('verlofsaldi')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'verlofsaldi'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Verlofsaldi
            </button>
          </nav>
        </div>

        {viewMode === 'verlofsaldi' ? (
          /* Paginabreed, niet per medewerker: de per-medewerker-variant van
             deze kaart praat met een endpoint dat HR/admin-only blijft. */
          <NmbrsLeaveBalanceCard showAll />
        ) : viewMode === 'werkrooster' ? (
          <TeamScheduleOverview />
        ) : (
        <div className="flex gap-6">
          {/* Team Members Sidebar */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 space-y-3">
                <h2 className="font-semibold text-gray-900">Teamleden ({teamMembers?.length || 0})</h2>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Zoek teamlid..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                {filteredTeamMembers && filteredTeamMembers.length > 0 ? (
                  filteredTeamMembers.map((member) => (
                    <button
                      key={member.id}
                      data-testid="team-member-card"
                      onClick={() => handleMemberClick(member)}
                      className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                        selectedMember?.id === member.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-medium">
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{member.name}</p>
                          <p className="text-sm text-gray-500 truncate">{member.email}</p>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    <p>{memberSearch ? 'Geen teamleden gevonden' : 'Geen teamleden'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {selectedMember ? (
              <div className="bg-white rounded-lg border border-gray-200">
                {/* Member Header */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-lg">
                        {selectedMember.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-gray-900">{selectedMember.name}</h2>
                      <p className="text-sm text-gray-500">{selectedMember.email}</p>
                    </div>
                    <Link
                      to={`/medewerkers/${selectedMember.employeeId}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Bekijk medewerkersprofiel"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Profiel
                    </Link>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px">
                    {(['notes', 'agreements', 'pop', 'evaluations', 'assessment-reports'] as const).map((tab) => (
                      <button
                        key={tab}
                        data-testid={`${tab}-tab`}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === tab
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab === 'notes' ? 'Notities' : tab === 'agreements' ? 'Afspraken' : tab === 'pop' ? 'POP' : tab === 'evaluations' ? 'Beoordelingen' : 'Verslagen'}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-4">
                  {activeTab === 'notes' && (
                    <div data-testid="notes-list">
                      {/* Add Note Button */}
                      {!showNoteForm && (
                        <button
                          data-testid="add-note-button"
                          onClick={() => setShowNoteForm(true)}
                          className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Notitie toevoegen
                        </button>
                      )}

                      {/* Note Form */}
                      {showNoteForm && (
                        <div className="mb-4 bg-gray-50 rounded-lg p-4">
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Type notitie
                              </label>
                              <select
                                data-testid="note-type-select"
                                value={noteVisibility}
                                onChange={(e) => setNoteVisibility(e.target.value as VisibilityType)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="private">Privé (alleen voor mij)</option>
                                <option value="shared">Gedeeld (zichtbaar voor medewerker en HR)</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Inhoud
                              </label>
                              <textarea
                                data-testid="note-content"
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Schrijf je notitie hier..."
                              />
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowNoteForm(false);
                                  setNoteContent('');
                                  setEditingNote(null);
                                }}
                                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                              >
                                Annuleren
                              </button>
                              <button
                                data-testid="save-note-button"
                                onClick={handleSaveNote}
                                disabled={!noteContent.trim() || createNote.isPending || updateNote.isPending}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                              >
                                {createNote.isPending || updateNote.isPending ? 'Opslaan...' : editingNote ? 'Bijwerken' : 'Opslaan'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Notes List */}
                      {notes && notes.length > 0 ? (
                        <div className="space-y-3">
                          {notes.map((note) => (
                            <div
                              key={note.id}
                              data-testid="note-item"
                              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  {note.isPrivate ? (
                                    <span data-testid="private-indicator" className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                                      🔒 Privé
                                    </span>
                                  ) : (
                                    <span data-testid="shared-indicator" className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-600">
                                      👥 Gedeeld
                                    </span>
                                  )}
                                  <span className="text-sm text-gray-500">
                                    {new Date(note.createdAt).toLocaleDateString('nl-NL')}
                                  </span>
                                </div>
                                <p className="text-gray-700 line-clamp-2">{note.content}</p>
                              </div>
                              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                <button
                                  onClick={() => handleEditNote(note)}
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Bewerken"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmNote(note)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Verwijderen"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">
                          Nog geen notities voor deze medewerker.
                        </p>
                      )}
                    </div>
                  )}

                  {activeTab === 'agreements' && (
                    <AgreementsTab employeeId={selectedMember.employeeId} />
                  )}

                  {activeTab === 'pop' && (
                    <div data-testid="pop-section">
                      <button
                        onClick={() => navigate(`/pop/${selectedMember.employeeId}`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        POP beheren
                      </button>
                    </div>
                  )}

                  {activeTab === 'evaluations' && (
                    <div>
                      {memberEvaluations.length > 0 ? (
                        <div className="space-y-3">
                          {memberEvaluations.map((evaluation) => (
                            <div
                              key={evaluation.id}
                              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
                            >
                              <div>
                                <p className="font-medium text-gray-900">
                                  {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL')} -{' '}
                                  {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL')}
                                </p>
                                <span
                                  className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                                    STATUS_LABELS[evaluation.status].color
                                  }`}
                                >
                                  {STATUS_LABELS[evaluation.status].label}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                {evaluation.totalScore && (
                                  <ScoreDisplay
                                    score={Math.round(parseFloat(evaluation.totalScore))}
                                    size="sm"
                                  />
                                )}
                                <button
                                  onClick={() => window.open(`/beoordeling/${evaluation.id}/print`, '_blank')}
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Scoringsformulier afdrukken"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                  </svg>
                                </button>
                                {/* Delete button - only for draft evaluations and non-medewerker roles */}
                                {canDeleteEvaluations && evaluation.status === 'draft' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteConfirmEvaluation(evaluation);
                                    }}
                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Beoordeling verwijderen"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                                <button
                                  onClick={() => navigate(`/beoordeling/${evaluation.id}/wizard`)}
                                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                  Bekijken
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">
                          Nog geen beoordelingen voor deze medewerker.
                        </p>
                      )}
                    </div>
                  )}

                  {activeTab === 'assessment-reports' && (
                    <AssessmentReportsTab employeeId={selectedMember.employeeId} />
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Selecteer een teamlid
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Klik op een teamlid in de lijst om notities, POP en beoordelingen te bekijken.
                </p>
              </div>
            )}
          </div>
        </div>
        )}

        {/* New Evaluation Modal */}
        {showNewEvalForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Nieuwe Beoordeling</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medewerker
                  </label>
                  <select
                    value={newEvalEmployeeId}
                    onChange={(e) => setNewEvalEmployeeId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecteer medewerker</option>
                    {teamMembers?.map((member) => (
                      <option key={member.id} value={member.employeeId}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start datum
                    </label>
                    <input
                      type="date"
                      value={newEvalPeriode.start}
                      onChange={(e) => setNewEvalPeriode((p) => ({ ...p, start: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Eind datum
                    </label>
                    <input
                      type="date"
                      value={newEvalPeriode.end}
                      onChange={(e) => setNewEvalPeriode((p) => ({ ...p, end: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setShowNewEvalForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleCreateEvaluation}
                  disabled={!newEvalEmployeeId || createEvaluation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createEvaluation.isPending ? 'Aanmaken...' : 'Start Beoordeling'}
                </button>
              </div>

              {createEvaluation.error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    {createEvaluation.error instanceof Error
                      ? createEvaluation.error.message
                      : 'Er ging iets mis bij het aanmaken van de beoordeling.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Note Confirmation Modal */}
        {deleteConfirmNote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Notitie verwijderen</h3>
              </div>

              <p className="text-gray-600 mb-2">
                Weet je zeker dat je deze notitie wilt verwijderen?
              </p>
              <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                "{deleteConfirmNote.content}"
              </p>
              <p className="text-sm text-red-600 mb-6">
                Deze actie kan niet ongedaan worden gemaakt.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmNote(null)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDeleteNote}
                  disabled={deleteNote.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteNote.isPending ? 'Verwijderen...' : 'Ja, verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Evaluation Confirmation Modal */}
        {deleteConfirmEvaluation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Beoordeling verwijderen</h3>
              </div>

              <p className="text-gray-600 mb-2">
                Weet je zeker dat je deze concept beoordeling wilt verwijderen?
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Periode: {new Date(deleteConfirmEvaluation.periodeStart).toLocaleDateString('nl-NL')} - {new Date(deleteConfirmEvaluation.periodeEnd).toLocaleDateString('nl-NL')}
              </p>
              <p className="text-sm text-red-600 mb-6">
                Deze actie kan niet ongedaan worden gemaakt.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmEvaluation(null)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDeleteEvaluation}
                  disabled={deleteEvaluation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteEvaluation.isPending ? 'Verwijderen...' : 'Ja, verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
